import { EmailRounded, FlashOn } from "@mui/icons-material";
import { Alert } from "@mui/material";
import { gql } from "graphql-request";
import { useEffect, useState } from "react";
import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  Create,
  CreateButton,
  Datagrid,
  DateInput,
  Edit,
  EditButton,
  FilterButton,
  FunctionField,
  List,
  NumberInput,
  Pagination,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SearchInput,
  SelectColumnsButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  TranslatableInputs,
  UrlField,
  WrapperField,
  required,
  useNotify,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { useMutation } from "react-query";
import { graphQLClient } from "../api";
import { CircularProgressWithLabel } from "../components/CircularProgressWithLabel";
import { LocaleReferenceArrayInput } from "../components/LocaleReferenceArrayInput";
import { SummaryTabHeader } from "../components/SummaryTabHeader";
import { TextArrayEmailInput } from "../components/TextArrayEmailInput";
import { TranslationsTabHeader } from "../components/TranslationsTabHeader";
import { isEmailValid, isNumeric } from "../components/utils";
import dataProvider from "../providers/grapheneDataProvider2";
import { Locale } from "./locales";
const userFilters = [<SearchInput source="name__icontains" alwaysOn={true} placeholder="Name" />];

export const EmailTemplateTranslationsForm = () => {
  const record = useRecordContext();
  const localeIds = useWatch({ name: "locales" });
  const [translationLocaleKeys, setTranslationLocaleKeys] = useState<string[] | null>(null);

  useEffect(() => {
    if (localeIds) {
      let t_keys: string[] = ["en-GB"];
      dataProvider.getMany("locales", { id__in: localeIds }).then(({ data }) => {
        data.forEach((locale: Locale) => {
          t_keys.push(`${locale.language}-${locale.country}`);
        });
        setTranslationLocaleKeys([...new Set(t_keys)]);
      });
    }
  }, [localeIds]);

  if (!localeIds || localeIds.length === 0) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        Please select locales
      </Alert>
    );
  }

  if (!translationLocaleKeys) {
    return <p>Loading...</p>;
  }
  return (
    <>
      <Alert severity="info">"en-GB" fields are required</Alert>
      <TranslatableInputs
        locales={translationLocaleKeys}
        defaultLocale={"en-GB"}
        sx={{ minWidth: "600px" }}
        groupKey="translations"
        fullWidth
      >
        <ReferenceInput source="translations.marketoCampaignId" reference="marketoCampaigns">
          <AutocompleteInput
            label="Marketo Campaign"
            optionText={(record) => (
              <div style={{ display: "unset " }}>
                <span>
                  <b>{record.name}</b> - #{record.id}
                </span>
                <br />
                <span>Workspace: {record.workspace}</span>
              </div>
            )}
            inputText={(record) => `${record.name} - #${record.id} | Workspace: ${record.workspace}`}
            helperText="Marketo Smart Campaign that triggers the template for this email type. List refreshes every half hour."
          />
        </ReferenceInput>

        <ReferenceField source="translations.marketoCampaignId" reference="marketoCampaigns" emptyText="">
          <UrlField source="computedUrl" target="_blank" />
        </ReferenceField>

        <TextInput source="translations.mailchimpTemplateName" label="Mailchimp Template Name" helperText="" />
        <TextInput source="translations.subjectText" label="Subject Text" helperText="" />
        <TextInput source="translations.previewText" label="Preview Text" helperText="" />
        <TextInput
          source="translations.fromEmail"
          label="From Email"
          helperText=""
          placeholder="no-reply@playbrush.com"
          focused
        />
        <TextInput
          source="translations.fromName"
          label="From Name"
          helperText=""
          placeholder="Playbrush GmbH"
          focused
        />
        <ArrayInput source="translations.attachments" label="Attachments">
          <SimpleFormIterator inline fullWidth>
            <TextInput source="fileName" sx={{ width: "20%" }} validate={required()} />
            <TextInput source="fileType" sx={{ width: "20%" }} validate={required()} />
            <TextInput
              source="fileLocation"
              sx={{ width: "55%" }}
              helperText="URL or relative path"
              validate={required()}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </TranslatableInputs>
    </>
  );
};

const TestEmailTabHeader = () => {
  return (
    <span style={{ display: "" }}>
      <FlashOn /> <span style={{ verticalAlign: "super" }}>Test Email</span>
    </span>
  );
};

const TestEmailToolbar = () => {
  return (
    <Toolbar>
      <SaveButton icon={<FlashOn />} label="Send Test Email" alwaysEnable />
    </Toolbar>
  );
};

const TestEmailForm = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const sendTestEmail = useMutation(
    "testEmailMutation",
    async (variables: {
      emailType: string;
      emails: string;
      locales: string;
      fieldValues: { field: string; value: any }[];
    }) => {
      const data = await graphQLClient.request(
        gql`
          mutation testEmailMutation(
            $emailType: String!
            $emails: [String]!
            $locales: [String]!
            $fieldValues: [TestEmailFieldValuesInputType!]!
          ) {
            testEmailMutation(
              emailType: $emailType
              emails: $emails
              locales: $locales
              fieldValues: $fieldValues
            ) {
              success
              errors
            }
          }
        `,
        variables
      );
      return data;
    },
    {
      onSuccess: ({ testEmailMutation }) => {
        if (testEmailMutation.errors && testEmailMutation.errors.length > 0) {
          let count = 1;
          let error_text = "";
          testEmailMutation.errors.forEach((error: any) => {
            error_text += ` ${count}. ${error} `;
            count += 1;
          });
          notify(error_text, { type: "error", multiLine: true });
        } else {
          notify("Test email sent successfully", { type: "success" });
        }
      },
      onError: (error: any) => {
        notify(`Error sending test email: ${error.message}`, { type: "error" });
      },
    }
  );

  const handleSendEmail = (data: any) => {
    let mutation_vars = {
      emailType: record.emailType,
      emails: data.testFields.emails.filter(isEmailValid),
      locales: data.testFields.locales,
      fieldValues: [] as Array<{ field: string; value: any }>,
    };
    delete data.testFields.emails;
    delete data.testFields.locales;
    for (let key in data.testFields) {
      if (isNumeric(key)) {
        continue;
      }
      mutation_vars.fieldValues.push({
        field: key,
        value: data.testFields[key],
      });
    }

    sendTestEmail.mutate(mutation_vars);
  };

  if (!record.testFields) {
    return <></>;
  }

  return (
    <SimpleForm onSubmit={handleSendEmail} toolbar={<TestEmailToolbar />} title="">
      <h2>
        <FlashOn color="secondary" sx={{ verticalAlign: "middle" }} />{" "}
        <EmailRounded color="primary" sx={{ verticalAlign: "middle" }} /> Test Email
      </h2>
      {record.testFields.map((field: any) => {
        switch (field.fieldType) {
          case "number":
            return (
              <NumberInput
                key={field.fieldName}
                source={`testFields.${field.fieldName}`}
                label={field.fieldLabel}
                fullWidth
                validate={required()}
              />
            );
          case "select":
            return (
              <SelectInput
                key={field.fieldName}
                source={`testFields.${field.fieldName}`}
                label={field.fieldLabel}
                choices={field.options || []}
                fullWidth
                validate={required()}
              />
            );
          case "date":
            return (
              <DateInput
                key={field.fieldName}
                source={`testFields.${field.fieldName}`}
                label={field.fieldLabel}
                fullWidth
                validate={required()}
                defaultValue={Date()}
              />
            );
          default:
            return (
              <TextInput
                key={field.fieldName}
                source={`testFields.${field.fieldName}`}
                label={field.fieldLabel}
                fullWidth
                validate={required()}
              />
            );
        }
      })}

      <ReferenceArrayInput
        source="testFields.locales"
        reference="locales"
        filter={{ id__in: record.locales }}
        format={(value) => value.id}
        validate={required()}
      >
        <AutocompleteArrayInput
          label="Template Locales"
          fullWidth
          optionText={(value) => (value === undefined ? "not defined" : value.id)}
          validate={required()}
        />
      </ReferenceArrayInput>
      <TextArrayEmailInput source="testFields.emails" isRequired={true} />
    </SimpleForm>
  );
};

export const EmailTemplatesEdit = () => {
  return (
    <>
      <Edit mutationMode="pessimistic" redirect={false}>
        <TabbedForm syncWithLocation={false} toolbar={<TemplateEditToolbar />}>
          <TabbedForm.Tab label={<SummaryTabHeader />}>
            <TextInput source="emailType" disabled fullWidth />
            <TextInput source="description" fullWidth validate={required()} />
            <LocaleReferenceArrayInput />
          </TabbedForm.Tab>
          <TabbedForm.Tab label={<TranslationsTabHeader />}>
            <EmailTemplateTranslationsForm />
          </TabbedForm.Tab>
        </TabbedForm>
      </Edit>
      <Edit sx={{ marginTop: "50px" }} mutationMode="pessimistic" redirect={false} title={""}>
        <TestEmailForm />
      </Edit>
    </>
  );
};

const SaveButtonWithMessage = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const onSuccess = (data: any) => {
    notify(`Template saved. ${data.updateMessage || ""}`, { type: data.updateMessage ? "warning" : "success" });
    // redirect("list", "emailTemplates");
  };
  return <SaveButton type="button" mutationOptions={{ onSuccess }} alwaysEnable />;
};

const TemplateEditToolbar = () => (
  <Toolbar>
    <SaveButtonWithMessage />
  </Toolbar>
);

export const EmailTemplatesCreate = () => {
  return (
    <Create>
      <TabbedForm syncWithLocation={false} toolbar={<TemplateEditToolbar />}>
        <TabbedForm.Tab label={<SummaryTabHeader />}>
          <TextInput source="emailType" disabled fullWidth />
          <TextInput source="description" fullWidth />
          <LocaleReferenceArrayInput />
        </TabbedForm.Tab>
        <TabbedForm.Tab label={<TranslationsTabHeader />}>
          <EmailTemplateTranslationsForm />
        </TabbedForm.Tab>
      </TabbedForm>
    </Create>
  );
};

export const MyActions = ({ showCreate = false }) => {
  return (
    <TopToolbar>
      {showCreate ? <CreateButton /> : ""}
      <FilterButton />
      <SelectColumnsButton />
    </TopToolbar>
  );
};

export const MyPagination = () => <Pagination rowsPerPageOptions={[25, 50, 100, 500, 1000]} />;
const filterToQueryTags = (searchText: string) => ({
  name__icontains: `${searchText}`,
});
export const emailTemplateFilters = [<TextInput label="Email Type" source="email_type__icontains" alwaysOn />];

export const EmailTemplatesList = (props: any) => {
  return (
    <List filters={emailTemplateFilters} actions={<MyActions {...props} />} pagination={<MyPagination />} perPage={25}>
      <Datagrid
        bulkActionButtons={false}
        sx={{
          "& .column-description": {
            maxWidth: 300,
            overflow: "hidden",
            textOverflow: "ellipsis",
            // whiteSpace: "nowrap",
          },
        }}
      >
        <TextField source="emailType" />
        <TextField source="description" sx={{ textOverflow: "ellipsis", maxWidth: "20px" }} />
        <ReferenceArrayField label="Locales" reference="locales" source="locales"></ReferenceArrayField>
        <FunctionField
          label="Marketo Configuration"
          render={(record: any) => {
            return (
              <CircularProgressWithLabel
                value={
                  record.translationsCount && record.languageLocales.length
                    ? (record.translationsCount / record.languageLocales.length) * 100
                    : 0
                }
              />
            );
          }}
        />
        <WrapperField label="Actions">
          <EditButton />
        </WrapperField>
      </Datagrid>
    </List>
  );
};
